import { TaskSubTicketResource } from 'dy-frontend-http-repository/lib/modules/TaskSubTicket/resources';
import { TaskSubTicketStatus } from 'dy-frontend-http-repository/lib/modules/TaskSubTicket/resources/TaskSubTicketResource';
import { useStore } from 'effector-react';
import React, { HTMLAttributes, useState } from 'react';

import DevText from '@app/components/Text';
import { Callout, Divider, Intent } from '@blueprintjs/core';

import { activeTimerSessionApi, activeTimerSessionLoadingApi } from '../../store/apis';
import {
    fetchActiveTimerSession,
    heartbeatTimerSession,
    startTimerSession,
    stopTimerSession,
    updateSubTicketStatus,
} from '../../store/effects';
import {
    SubTicketRecoveryScope,
    reconcileSubTicketRef,
    refreshSubTicketViews,
    reportSubTicketMutation,
    showSubTicketFailure,
    showSubTicketRecoveryOffer,
} from '../../store/reconcile';
import { ACTIVE_SESSION_KEY, nextGeneration } from '../../store/requestGeneration';
import { $activeTimerSession, $activeTimerSessionError, $isActiveTimerSessionLoading } from '../../store/states';
import {
    ReportCustody,
    TimerOperationDeps,
    TimerOperationResult,
    startTimerOnSubTicket,
    stopRunningTimer,
    switchTimerToSubTicket,
} from '../../store/timerOperations';
import { SubTicketCapabilities, formatLoggedMinutes, resolveSubTicketConflict } from '../../utils';
import StatusSegmentedControl from '../StatusSegmentedControl';
import TimerControl from '../TimerControl';

export interface DesignerWorkspaceProps {
    subTicket: TaskSubTicketResource;
    capabilities: SubTicketCapabilities;
    isArchivedShown: boolean;
    expandedSubTicketId: ID | null;

    // Resolved once by the page for the single phase a timer is running on, rather than by each
    // row independently — N rendered rows must not issue N identical detail reads
    runningElsewhereLabel: string | null;
    runningElsewhereTaskId: ID | null;
}

export type Props = HTMLAttributes<HTMLDivElement> & DesignerWorkspaceProps;

// Exactly two operational regions, each gated on its own capability, so a manager holding
// STATUS_MANAGE sees the status selector without ever being shown timer controls.
//
// Starting a timer never changes the status, stopping never changes it, and changing the status
// never touches the timer.
const DesignerWorkspace: React.FC<Props> = ({
    subTicket,
    capabilities,
    isArchivedShown,
    expandedSubTicketId,
    runningElsewhereLabel,
    runningElsewhereTaskId,
    ...props
}) => {
    const activeSession = useStore($activeTimerSession);
    const isActiveSessionLoading = useStore($isActiveTimerSessionLoading);
    const activeSessionError = useStore($activeTimerSessionError);

    const [isBusy, setIsBusy] = useState(false);
    const [isStatusChanging, setIsStatusChanging] = useState(false);
    const [timerErrorMessage, setTimerErrorMessage] = useState<string | null>(null);

    // Reported alongside the message, never inferred from it: only the operation that just ran
    // knows whether the words the designer typed are still in the box or were saved against a
    // session that has now closed
    const [reportCustody, setReportCustody] = useState<ReportCustody>('none');

    // Carried alongside the custody so the callout can state how much the committed stop logged.
    // A designer whose switch half-failed has lost the previous session from the screen, and this
    // is the only place that figure is still visible to them.
    const [loggedDurationMin, setLoggedDurationMin] = useState<number | null>(null);

    // Deliberately separate from the timer error. A STATUS_MANAGE manager has no timer region
    // mounted at all, and a Complete-while-running 409 is exactly the conflict they most need
    // to see — routing it through TimerControl would make it invisible to them.
    const [statusErrorMessage, setStatusErrorMessage] = useState<string | null>(null);

    const refresh = () =>
        refreshSubTicketViews({
            taskId: subTicket.task_id,
            isArchivedShown,
            expandedSubTicketId,
        });

    // Every stale-view retry in this workspace repairs the same thing through the same shared
    // handler; only the name of what committed differs
    const recoveryScope = (what: string, committedDetail: string | null = null): SubTicketRecoveryScope => ({
        what,
        taskId: subTicket.task_id,
        refreshViews: refresh,
        committedDetail,
    });

    // How much the server logged, phrased once so the success toast and the stale-view offer
    // cannot drift apart
    const loggedDetail = (minutes: number | null) =>
        minutes === null ? null : `${formatLoggedMinutes(minutes)} logged`;

    // The authoritative read. Its generation is the newest of its key, so only its own reply can
    // settle restoration — a stale one cannot re-enable Start underneath it. The resolved value
    // is returned as well as stored, because whether a start committed is answered by inspecting
    // it, not by writing it somewhere and hoping.
    const readActiveSession = () => fetchActiveTimerSession({ generation: nextGeneration(ACTIVE_SESSION_KEY) });

    const timerDeps: TimerOperationDeps = {
        stopSession: (id, comment) => stopTimerSession({ id, input: { comment } }),
        startSession: (subTicketId) => startTimerSession(subTicketId),
        onSessionStarted: (session) => activeTimerSessionApi.set(session),
        onSessionCleared: () => activeTimerSessionApi.clear(),
        // Returns view truth so a committed timer write is never reported as failed just
        // because the list could not be re-read afterwards
        refresh: async () => ({ ok: (await refresh()).ok }),
        beginRestore: () => activeTimerSessionLoadingApi.beginRestore(),
        restoreActiveSession: () => readActiveSession(),
    };

    // A Start refused with a 409 names the offending phase in its own response, which is more
    // specific than the generic conflict copy. Everything else is decided by the authoritative
    // re-read inside `startTimerOnSubTicket`.
    const startErrorMessage = (result: TimerOperationResult, error: unknown): string | null => {
        if (result.startRecovery !== 'running-elsewhere') {
            return result.errorMessage;
        }

        return resolveSubTicketConflict(error, 'start_timer')?.message ?? result.errorMessage;
    };

    const handleStart = async () => {
        setIsBusy(true);
        setTimerErrorMessage(null);
        setReportCustody('none');
        setLoggedDurationMin(null);

        let thrown: unknown = null;
        const deps: TimerOperationDeps = {
            ...timerDeps,
            startSession: async (id) => {
                try {
                    return await startTimerSession(id);
                } catch (e) {
                    thrown = e;
                    throw e;
                }
            },
        };

        const result = await startTimerOnSubTicket(subTicket.id, deps);

        setTimerErrorMessage(startErrorMessage(result, thrown));
        setReportCustody(result.reportCustody);
        setLoggedDurationMin(result.loggedDurationMin);
        setIsBusy(false);

        // A start that the recovery read proved committed is a success, however it was reported
        // on the wire. Only the view being stale is worth saying anything about.
        if (result.didStart && !result.refreshOk) {
            showSubTicketRecoveryOffer('views', recoveryScope('The timer'));
        }
    };

    // A write that never ran is reported as one: nothing committed, nothing to clear
    const noWrite: TimerOperationResult = {
        ok: false,
        didStop: false,
        didStart: false,
        errorMessage: null,
        startRecovery: 'not-attempted',
        recoveredSession: null,
        loggedDurationMin: null,
        reportCustody: 'none',
        refreshOk: true,
        refreshAttempted: false,
    };

    const handleStopAndStartHere = async (previousSessionComment: string): Promise<TimerOperationResult> => {
        if (!activeSession) {
            return noWrite;
        }

        setIsBusy(true);
        setTimerErrorMessage(null);

        const result = await switchTimerToSubTicket(activeSession, subTicket.id, previousSessionComment, timerDeps);

        setTimerErrorMessage(result.errorMessage);
        setReportCustody(result.reportCustody);
        setLoggedDurationMin(result.loggedDurationMin);
        setIsBusy(false);

        const logged = loggedDetail(result.loggedDurationMin);

        if (result.ok) {
            reportSubTicketMutation({
                successMessage: logged
                    ? `Previous timer saved — ${logged} — and a new timer started here.`
                    : 'Previous timer saved and a new timer started here.',
                views: { ok: result.refreshOk },
                reconciled: null,
                scope: recoveryScope('The timer switch', logged),
            });
        } else if (result.didStop && !result.refreshOk) {
            // The stop committed even though the switch as a whole did not. The timer callout is
            // already stating both the start failure and how much the previous session logged, so
            // this reports ONLY the stale view — repeating the figure here would be two messages
            // about one fact.
            showSubTicketRecoveryOffer('views', recoveryScope('The previous timer'));
        }

        // The whole result goes back: the caller clears the previous session's report text on the
        // STOP having committed, which is not the same question as whether the switch completed
        return result;
    };

    const handleStop = async (comment: string): Promise<TimerOperationResult> => {
        if (!activeSession) {
            return noWrite;
        }

        setIsBusy(true);
        setTimerErrorMessage(null);

        const result = await stopRunningTimer(activeSession, comment, timerDeps);

        setTimerErrorMessage(result.errorMessage);
        setReportCustody(result.reportCustody);
        setLoggedDurationMin(result.loggedDurationMin);
        setIsBusy(false);

        if (result.ok) {
            // The stop committed. Reporting a failure here would invite the designer to stop a
            // session that no longer exists.
            //
            // The logged amount is the point of the whole operation, so it is stated whether or
            // not the list could be re-read: `recoveryScope` carries it into the stale-view
            // message too. A repeat of a stop that already landed is idempotent server-side and
            // returns the same figure, so it reads identically.
            const logged = loggedDetail(result.loggedDurationMin);

            reportSubTicketMutation({
                successMessage: logged
                    ? `Timer stopped and the work report saved — ${logged}.`
                    : 'Timer stopped and the work report saved.',
                views: { ok: result.refreshOk },
                reconciled: null,
                scope: recoveryScope('The timer and its work report', logged),
            });
        }

        return result;
    };

    const handleHeartbeat = (sessionId: ID) => {
        heartbeatTimerSession(sessionId).catch((e) => {
            // A missed ping is not user-actionable: the next one is 60s away, and the server
            // finalises an abandoned session at its last heartbeat regardless
            console.error(e);
        });
    };

    const handleChangeStatus = async (status: TaskSubTicketStatus) => {
        setIsStatusChanging(true);
        setStatusErrorMessage(null);

        try {
            const ref = await updateSubTicketStatus({ id: subTicket.id, input: { status } });
            const reconciled = await reconcileSubTicketRef(ref, subTicket.task_id);

            const views = await refresh();

            reportSubTicketMutation({
                successMessage: 'Phase status updated.',
                views,
                reconciled,
                scope: recoveryScope('The phase status'),
            });
        } catch (e) {
            // Completing is refused while a timer runs on this sub-ticket. Surfaced in the
            // status region AND as a toast, so it reaches a caller with no timer region.
            const conflict = resolveSubTicketConflict(e, 'complete');

            if (conflict) {
                setStatusErrorMessage(conflict.message);
                showSubTicketFailure(conflict.message);
            } else {
                setStatusErrorMessage('The phase status could not be updated. Please try again.');
                showSubTicketFailure('The phase status could not be updated. Please try again.');
                console.error(e);
            }
        } finally {
            setIsStatusChanging(false);
        }
    };

    const isTimerRegionVisible = capabilities.canStartTimer || capabilities.canStopTimer;

    // A phase nobody can act on renders no operational region at all
    if (!isTimerRegionVisible && !capabilities.canManageStatus) {
        return null;
    }

    return (
        <div {...props}>
            {/* Operational region 1 of 2 — server-backed work report logger / timer */}
            {isTimerRegionVisible && (
                <TimerControl
                    subTicket={subTicket}
                    activeSession={activeSession}
                    isActiveSessionLoading={isActiveSessionLoading}
                    hasActiveSessionError={activeSessionError}
                    canStartTimer={capabilities.canStartTimer}
                    canStopTimer={capabilities.canStopTimer}
                    canStopThenStartHere={capabilities.canStopThenStartHere}
                    isBusy={isBusy}
                    errorMessage={timerErrorMessage}
                    reportCustody={reportCustody}
                    loggedDurationMin={loggedDurationMin}
                    runningElsewhereLabel={runningElsewhereLabel}
                    runningElsewhereTaskId={runningElsewhereTaskId}
                    onStart={handleStart}
                    onStopAndStartHere={handleStopAndStartHere}
                    onStop={handleStop}
                    onHeartbeat={handleHeartbeat}
                    onDismissError={() => {
                        setTimerErrorMessage(null);
                        setReportCustody('none');
                        setLoggedDurationMin(null);
                    }}
                />
            )}

            {isTimerRegionVisible && capabilities.canManageStatus && <Divider className="mt-2 mb-2" />}

            {/* Operational region 2 of 2 — standalone status selector */}
            {capabilities.canManageStatus && (
                <div>
                    {statusErrorMessage && (
                        <Callout className="mb-2" intent={Intent.DANGER} title="Cannot change the status right now">
                            <DevText>{statusErrorMessage}</DevText>
                        </Callout>
                    )}

                    <StatusSegmentedControl
                        value={subTicket.status}
                        isChanging={isStatusChanging}
                        onChange={handleChangeStatus}
                    />
                </div>
            )}
        </div>
    );
};

export default DesignerWorkspace;
